import type {
  Caractere,
  Celula,
  ColunaSugestao,
  PalavraCanonica,
  PalavraColocada,
  Sugestao,
} from '../tipos';
import type { Semente } from '../aleatorio/rng';
import { derivar, inteiroAte, proximo, sementeDaCelula } from '../aleatorio/rng';
import { ehHifen } from '../lexico/alfabeto';
import type { IndiceLexico } from '../lexico/indice';
import { caracteresCanonicos, chaveCelula } from '../lexico/normalizacao';
import { verificarColocacao } from '../tabuleiro/colisao';
import { celulaNoIndice, mesmaCelula, origemPeloCruzamento } from '../tabuleiro/coordenadas';
import type { Grade } from '../tabuleiro/grade';
import {
  preferenciaVisual,
  pontuarPosicaoVertical,
  sustentaProximaHorizontal,
} from '../tabuleiro/posicionamento';
import type { FilaNovidade } from './novidade';
import { idadeDe } from './novidade';

/**
 * Geração de sugestões verticais (§4.2, §5, §9, §10).
 *
 * Propriedades garantidas, e verificadas em `tests/propriedades`:
 *
 *  - toda sugestão contém a letra cruzada exatamente na posição declarada;
 *  - nenhuma colocação aceita viola as regras de colisão de §9;
 *  - o resultado é função pura de (semente, célula, base, fila, grade);
 *  - a mesma entrada devolve a mesma palavra, mesmo após desfazer.
 */

/**
 * Teto de candidatas examinadas por posição de cruzamento.
 *
 * Sem ele, uma letra frequente varreria dezenas de milhares de palavras a cada
 * tecla e estouraria o orçamento de 100 ms do p95 (§16). A amostragem é feita
 * com passo co-primo sobre a lista ordenada: determinística e bem espalhada
 * pelo alfabeto, ao contrário de uma fatia contígua.
 */
export const LIMITE_CANDIDATAS_POR_POSICAO = 96;

const PASSOS_CANDIDATOS = [1, 7, 11, 13, 17, 19, 23, 29, 31, 37, 41, 43, 47, 53, 59, 61];

/** Id estável de uma sugestão: uma por célula de cruzamento. */
function idDaSugestao(chave: string): string {
  return `s${chave}`;
}

export interface ContextoSugestao {
  readonly indice: IndiceLexico;
  readonly grade: Grade;
  readonly sementeSessao: Semente;
  readonly fila: FilaNovidade;
  /** Texto canônico da palavra-base em edição. */
  readonly base: PalavraCanonica;
  /** Célula da primeira letra da palavra-base. */
  readonly origemBase: Celula;
  /** Células da base já atravessadas por uma ponte anterior (§5.13, T06-02). */
  readonly celulasJaCruzadas: readonly Celula[];
  /**
   * Textos já no tabuleiro (R5-15).
   *
   * O jogo não pode sugerir o que ele mesmo proíbe: seria oferecer uma jogada
   * que a regra recusa, e ainda por cima com a mão do sistema.
   */
  readonly usadas: ReadonlySet<PalavraCanonica>;
}

interface Candidato {
  readonly texto: PalavraCanonica;
  readonly indiceNoTexto: number;
  readonly naFila: boolean;
  readonly idade: number;
  readonly pontuacao: number;
}

function mdc(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x;
}

function passoCoPrimo(total: number, semente: Semente): number {
  for (let i = 0; i < PASSOS_CANDIDATOS.length; i += 1) {
    const bruto =
      PASSOS_CANDIDATOS[
        (inteiroAte(semente, PASSOS_CANDIDATOS.length) + i) % PASSOS_CANDIDATOS.length
      ];
    if (bruto !== undefined && mdc(bruto, total) === 1) return bruto;
  }
  return 1;
}

/** Reúne uma amostra determinística de candidatas para uma letra. */
function coletarCandidatos(
  ctx: ContextoSugestao,
  letra: Caractere,
  semente: Semente,
  indiceNaBase: number,
): readonly Candidato[] {
  const preferencia = preferenciaVisual(ctx.sementeSessao, indiceNaBase);
  const saida: Candidato[] = [];

  for (const posicao of ctx.indice.posicoesDe(letra)) {
    const total = ctx.indice.quantidade(letra, posicao);
    if (total === 0) continue;

    const sementePosicao = derivar(semente, 'pos', posicao);
    const inicio = inteiroAte(sementePosicao, total);
    const salto = passoCoPrimo(total, sementePosicao);
    const limite = Math.min(total, LIMITE_CANDIDATAS_POR_POSICAO);

    for (let k = 0; k < limite; k += 1) {
      const texto = ctx.indice.candidataEm(letra, posicao, (inicio + k * salto) % total);
      if (texto === undefined) continue;
      // Nenhuma sugestão pode ser igual à palavra-base atual (§4.2) nem a
      // nada que já esteja no tabuleiro (R5-15).
      if (texto === ctx.base) continue;
      if (ctx.usadas.has(texto)) continue;

      const tamanho = caracteresCanonicos(texto).length;
      const idade = idadeDe(ctx.fila, texto);
      const ruido = proximo(derivar(sementePosicao, texto, k)).valor * 0.12;

      saida.push({
        texto,
        indiceNoTexto: posicao,
        naFila: Number.isFinite(idade),
        idade,
        pontuacao: pontuarPosicaoVertical(posicao, tamanho, preferencia) + ruido,
      });
    }
  }

  return saida;
}

/**
 * Ordem de tentativa de §9:
 *   1. candidato novo na posição visual preferida;
 *   2. outra ocorrência da letra no mesmo candidato;
 *   3. candidato novo em direção oposta;
 *   4. candidato da fila recente, do mais antigo para o mais recente;
 *   5. sem sugestão.
 *
 * Os passos 1–3 caem no mesmo balde, separados pela pontuação de posição;
 * o passo 4 é o balde seguinte, ordenado por idade.
 */
function ordenarCandidatos(candidatos: readonly Candidato[]): readonly Candidato[] {
  return [...candidatos].sort((a, b) => {
    if (a.naFila !== b.naFila) return a.naFila ? 1 : -1;
    if (a.naFila && b.naFila && a.idade !== b.idade) return a.idade - b.idade;
    if (b.pontuacao !== a.pontuacao) return b.pontuacao - a.pontuacao;
    return a.texto < b.texto ? -1 : 1;
  });
}

/**
 * Gera a coluna de uma letra da palavra-base.
 *
 * O resultado é função pura de (semente da sessão, célula, texto da base, fila
 * de recentes, grade): a mesma entrada devolve sempre a mesma vertical, que é o
 * que §10 e T03-05 exigem para desfazer e restaurar.
 */
export function gerarColuna(ctx: ContextoSugestao, indiceNaBase: number): ColunaSugestao {
  const letras = caracteresCanonicos(ctx.base);
  const letra = letras[indiceNaBase];
  const cruzamento = celulaNoIndice(ctx.origemBase, 'horizontal', indiceNaBase);

  if (letra === undefined || ehHifen(letra)) {
    return {
      tipo: 'indisponivel',
      coluna: { cruzamento, indiceNaBase, motivo: 'hifen' },
    };
  }

  // A célula já atravessada pela ponte anterior não recebe segunda vertical
  // (§5.13, T06-02).
  if (ctx.celulasJaCruzadas.some((c) => mesmaCelula(c, cruzamento))) {
    return {
      tipo: 'indisponivel',
      coluna: { cruzamento, indiceNaBase, motivo: 'ja-cruzada' },
    };
  }

  const chave = chaveCelula(cruzamento.coluna, cruzamento.linha);
  // A vertical é função do draft (§10, ADR-0007), e o que entra na semente é o
  // **prefixo** da base até esta letra — não só a célula, e não a base inteira.
  //
  // Só a célula era o bug: com `A` na quarta casa, CARAS, VARAS e MALAS caíam
  // na mesma semente e recebiam sempre a mesma vertical, porque a célula é a
  // mesma. A base inteira seria o excesso oposto: cada tecla digitada depois
  // desta letra re-sortearia a coluna, e T02-03 pede o contrário — apagar uma
  // letra não mexe nas sugestões das outras.
  //
  // O prefixo dá as duas coisas: CARA ≠ VARA troca a coluna do A, e digitar o
  // S no fim não troca nada do que já está na tela.
  const prefixo = letras.slice(0, indiceNaBase + 1).join('');
  const semente = derivar(sementeDaCelula(ctx.sementeSessao, chave), prefixo);
  const ordenados = ordenarCandidatos(coletarCandidatos(ctx, letra, semente, indiceNaBase));

  if (ordenados.length === 0) {
    return {
      tipo: 'indisponivel',
      coluna: { cruzamento, indiceNaBase, motivo: 'sem-candidato' },
    };
  }

  // O primeiro candidato que couber espacialmente vence.
  for (let k = 0; k < ordenados.length; k += 1) {
    const candidato = ordenados[k];
    if (candidato === undefined) continue;

    const letrasCandidato = caracteresCanonicos(candidato.texto);
    const origem = origemPeloCruzamento(cruzamento, 'vertical', candidato.indiceNoTexto);
    const resultado = verificarColocacao(
      ctx.grade,
      letrasCandidato,
      origem,
      'vertical',
      ctx.indice,
      { celula: cruzamento, indiceNoTexto: candidato.indiceNoTexto },
    );
    if (!resultado.ok) continue;

    // Última regra de §9: a ponte precisa sustentar a próxima horizontal. Uma
    // vertical que cabe mas não deixa nenhuma letra utilizável é um beco sem
    // saída — o jogador a escolheria e ficaria sem âncora para continuar.
    const pontePotencial: PalavraColocada = {
      id: idDaSugestao(chave),
      texto: candidato.texto,
      origem,
      orientacao: 'vertical',
      papel: 'ponte',
      turno: 0,
    };
    if (!sustentaProximaHorizontal(pontePotencial, cruzamento, ctx.indice, ctx.grade)) continue;

    const sugestao: Sugestao = {
      id: idDaSugestao(chave),
      texto: candidato.texto,
      cruzamento,
      indiceNaBase,
      indiceNoTexto: candidato.indiceNoTexto,
      origem,
    };
    return { tipo: 'sugestao', sugestao };
  }

  // Esgotou ocorrências, candidatos e relaxamento da fila (T08-04).
  return {
    tipo: 'indisponivel',
    coluna: { cruzamento, indiceNaBase, motivo: 'sem-candidato' },
  };
}

/**
 * Gera todas as colunas da palavra-base.
 *
 * As colunas são independentes: apagar uma letra não muda as sugestões das
 * outras (T02-03), porque cada uma depende só da própria célula. A grade,
 * porém, é compartilhada — sugestões geradas aqui podem
 * colidir entre si e isso é resolvido em `resolverColisoesEntreColunas`.
 */
export function gerarColunas(ctx: ContextoSugestao): readonly ColunaSugestao[] {
  const letras = caracteresCanonicos(ctx.base);
  const saida: ColunaSugestao[] = [];
  for (let i = 0; i < letras.length; i += 1) {
    saida.push(gerarColuna(ctx, i));
  }
  return resolverColisoesEntreColunas(saida);
}

/**
 * Duas sugestões vizinhas podem ocupar a mesma célula. A grade só conhece
 * palavras confirmadas, então a checagem entre sugestões acontece aqui:
 * a coluna mais à esquerda tem prioridade e as demais recuam para
 * indisponível, em vez de renderizar letras sobrepostas.
 */
export function resolverColisoesEntreColunas(
  colunas: readonly ColunaSugestao[],
): readonly ColunaSugestao[] {
  const ocupadas = new Set<string>();
  const saida: ColunaSugestao[] = [];

  for (const coluna of colunas) {
    if (coluna.tipo !== 'sugestao') {
      saida.push(coluna);
      continue;
    }

    const { sugestao } = coluna;
    const letras = caracteresCanonicos(sugestao.texto);
    const chaves: string[] = [];
    let conflita = false;

    for (let i = 0; i < letras.length; i += 1) {
      if (i === sugestao.indiceNoTexto) continue;
      const c = celulaNoIndice(sugestao.origem, 'vertical', i);
      const k = chaveCelula(c.coluna, c.linha);
      if (ocupadas.has(k)) {
        conflita = true;
        break;
      }
      chaves.push(k);
    }

    if (conflita) {
      saida.push({
        tipo: 'indisponivel',
        coluna: {
          cruzamento: sugestao.cruzamento,
          indiceNaBase: sugestao.indiceNaBase,
          motivo: 'sem-candidato',
        },
      });
      continue;
    }

    for (const k of chaves) ocupadas.add(k);
    saida.push(coluna);
  }

  return saida;
}

/** Há ao menos uma sugestão selecionável? Separa T03 de T08. */
export function temSugestaoDisponivel(colunas: readonly ColunaSugestao[]): boolean {
  return colunas.some((c) => c.tipo === 'sugestao');
}
